import { useState, useMemo, useEffect, useRef } from "react";
import { downloadMapWorkbook } from "../../utils/exportMapPins";
import { MAP_LAYER_COLORS } from "../../constants/mapLayers";
import { fmt } from "../../utils/formatters";
import { pinField } from "../../utils/pinFields";
import { leaseCompsNear } from "../../utils/geometry";
import { loadRentRoll } from "../../supabase";

// ═══════════════════════════════════════════════════════════════════════════
// MAP LIST VIEW
// ═══════════════════════════════════════════════════════════════════════════
// CoStar-style list counterpart to the map. `pins` is the exact array the
// marker layer drew, so every sidebar filter — layers, search, market, radius,
// boundary — is already applied and the two views can never disagree.
//
// Rows are split into entity tabs because a sourcing property, a deal and a
// lease comp share almost no columns; one merged table would be mostly blanks.

const PAGE_SIZE = 50;
const COLS_STORAGE_KEY = "pp-map-list-cols";
const CHECKBOX_W = 38;

const dash = "—";

const asNum   = v => (v == null || v === "" || isNaN(Number(v)) ? null : Number(v));
const showNum = v => { const n = asNum(v); return n == null ? dash : n.toLocaleString(); };
const showMon = v => { const n = asNum(v); return n == null ? dash : fmt.currencyOrDash(n); };
const showPct = v => { const n = asNum(v); return n == null ? dash : `${n.toFixed(1)}%`; };
const showPsf = v => { const n = asNum(v); return n == null ? dash : `$${n.toFixed(2)}`; };
const showTxt = v => (v == null || v === "" ? dash : String(v));
const showDate = v => {
  if (!v) return dash;
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const NUMERIC_TYPES = new Set(["num", "money", "pct", "psf", "norm"]);
const isNumericCol = c => NUMERIC_TYPES.has(c.t);

// col: { key, label, w (min px), t: type for sort/align/format, get }
const C = (key, label, w, t, get) => ({ key, label, w, t, get: get || (p => pinField(p, key)) });


const TABS = [
  {
    id: "deals",
    label: "Active Deals",
    // Outreach is the sourcing track, not a pipeline stage, so it has its own
    // tab. Tracking stays in here rather than being excluded: it is off the
    // map by default, and if you switch the layer on you should get rows
    // rather than pins with no list entry.
    match: p => p.isPipeline && p.layerKey !== "outreach" && p.layerKey !== "sourcing",
    cols: [
      C("_dealName", "Deal", 200, "text"),
      C("address", "Address", 220, "text"),
      C("market", "Market", 135, "text"),
      C("stage", "Stage", 130, "text"),
      C("sf", "SF", 105, "num"),
      C("askingPrice", "Asking", 115, "money"),
      C("capRate", "Cap", 74, "pct"),
      C("underwrittenPrice", "UW Price", 115, "money"),
      C("leveredIrr", "Lev IRR", 90, "pct"),
      C("equityMultiple", "EM", 70, "num"),
      C("trendedYoc", "Tr YoC", 85, "pct"),
      C("untrendedYoc", "Untr YoC", 95, "pct"),
      C("product_type", "Product", 125, "text"),
      C("year_built", "Built", 74, "text"),
      C("clear_height", "Clear Ht", 90, "text"),
      C("opex_psf", "OPEX $/SF", 100, "psf"),
    ],
    presets: {
      Underwriting: ["_dealName", "sf", "askingPrice", "capRate", "underwrittenPrice", "leveredIrr", "equityMultiple", "trendedYoc", "untrendedYoc", "opex_psf"],
      Pipeline:     ["_dealName", "address", "market", "stage", "sf", "askingPrice", "capRate"],
    },
    totals: [
      { key: "sf", label: "Total SF", kind: "sum", fmt: showNum },
      { key: "askingPrice", label: "Total Ask", kind: "sum", fmt: showMon },
    ],
  },
  {
    id: "outreach",
    label: "Outreach",
    match: p => p.isPipeline && (p.layerKey === "outreach" || p.layerKey === "sourcing"),
    cols: [
      C("_dealName", "Deal", 200, "text"),
      C("address", "Address", 230, "text"),
      C("market", "Market", 135, "text"),
      C("sf", "SF", 105, "num"),
      C("askingPrice", "Asking", 115, "money"),
      C("capRate", "Cap", 74, "pct"),
      C("year_built", "Built", 74, "text"),
      C("clear_height", "Clear Ht", 90, "text"),
      C("opex_psf", "OPEX $/SF", 100, "psf"),
    ],
    presets: {
      Compact: ["_dealName", "address", "market", "sf"],
    },
    totals: [{ key: "sf", label: "Total SF", kind: "sum", fmt: showNum }],
  },
  {
    id: "sale",
    label: "Sale Comps",
    match: p => p.isComp && p.compType === "sale",
    cols: [
      C("address", "Address", 230, "text"),
      C("market", "Market", 135, "text"),
      C("property_name", "Property", 175, "text"),
      C("buyer", "Buyer", 175, "text"),
      C("seller", "Seller", 175, "text"),
      C("sf", "Building SF", 110, "num"),
      C("sale_price", "Sale Price", 118, "money"),
      C("price_psf", "$/SF", 88, "psf"),
      C("cap_rate", "Cap", 74, "pct"),
      C("occupancy_at_closing", "Occ @ Close", 110, "pct"),
      C("walt_years", "WALT", 74, "num"),
      C("year_built", "Built", 84, "text"),
      C("clear_height", "Clear Ht", 94, "text"),
      C("comp_date", "Date", 104, "date"),
      C("status_detail", "Status", 135, "text"),
      C("source", "Source", 125, "text"),
    ],
    presets: {
      Pricing:  ["address", "market", "sf", "sale_price", "price_psf", "cap_rate", "comp_date"],
      Building: ["address", "market", "sf", "year_built", "clear_height", "occupancy_at_closing", "walt_years"],
      Parties:  ["address", "property_name", "buyer", "seller", "comp_date", "source"],
    },
    totals: [
      { key: "sf", label: "Total SF", kind: "sum", fmt: showNum },
      { key: "price_psf", label: "Avg $/SF", kind: "avg", fmt: showPsf },
      { key: "cap_rate", label: "Avg Cap", kind: "avg", fmt: showPct },
    ],
  },
  {
    id: "lease",
    label: "Lease Comps",
    match: p => p.isComp && p.compType === "lease",
    cols: [
      C("address", "Address", 230, "text"),
      C("market", "Market", 135, "text"),
      C("property_name", "Property", 175, "text"),
      C("tenant", "Tenant", 175, "text"),
      C("tenant_industry", "Industry", 145, "text"),
      C("sf", "Building SF", 110, "num"),
      C("suite_sf", "Suite SF", 100, "num"),
      C("lease_rate", "Rate $/SF", 105, "psf"),
      C("lease_type", "Type", 115, "lease"),
      C("opex_psf", "OPEX $/SF", 105, "psf"),
      C("_normRate", "Norm $/SF", 130, "norm", (p, basis) => normalizeRate(p, basis)),
      C("comp_date", "Date", 104, "date"),
      C("landlord", "Landlord", 165, "text"),
      C("loading_type", "Loading", 115, "text"),
      C("door_type", "Door", 104, "text"),
      C("source", "Source", 125, "text"),
    ],
    presets: {
      Economics: ["address", "market", "tenant", "sf", "suite_sf", "lease_rate", "lease_type", "opex_psf", "_normRate", "comp_date"],
      Building:  ["address", "market", "sf", "loading_type", "door_type", "landlord"],
      Tenants:   ["address", "tenant", "tenant_industry", "suite_sf", "lease_rate", "comp_date"],
    },
    totals: [
      { key: "sf", label: "Total SF", kind: "sum", fmt: showNum },
      { key: "lease_rate", label: "Avg Rate", kind: "avg", fmt: showPsf },
      { key: "_normRate", label: "Avg Norm", kind: "avg", fmt: showPsf },
    ],
  },
];

// Lease-type badge colors mirror CompsView so a lease reads the same
// everywhere in Proxy.
const LEASE_COLORS = {
  NNN: "#8a8a8a",
  "Modified Gross": "#b4b4b4",
  MG: "#b4b4b4",
  Gross: "#4f9d76",
  BY: "#c8922f",
};

// ── Lease normalization ────────────────────────────────────────────────────
// Puts every lease comp on one basis so they can be compared and sorted.
// Target basis is NNN by default, flipped by the Basis toggle.
//
// The important case is a Gross comp with no OPEX on file: there is no way to
// net it down, so rather than blanking the cell we show the raw rate and label
// its basis honestly as Gross. Every row gets a number; every number says
// which basis it is actually on.
function normalizeRate(pin, target) {
  const rate = asNum(pinField(pin, "lease_rate"));
  if (rate == null) return null;
  const opex = asNum(pinField(pin, "opex_psf"));
  const type = String(pinField(pin, "lease_type") || "").trim();
  const isNNN   = /^nnn$/i.test(type);
  const isGross = /^gross$/i.test(type);
  const isMG    = /^(mg|modified\s*gross)$/i.test(type);

  if (target === "gross") {
    if (isGross) return { value: rate, basis: "Gross" };
    if (isNNN || isMG) {
      if (opex == null) return { value: rate, basis: isNNN ? "NNN" : "MG", unconverted: true };
      return { value: rate + opex, basis: "Gross", approx: isMG };
    }
    return { value: rate, basis: type || null, unconverted: true };
  }

  // Target NNN
  if (isNNN) return { value: rate, basis: "NNN" };
  if (isGross || isMG) {
    if (opex == null) return { value: rate, basis: "Gross", unconverted: true };
    return { value: rate - opex, basis: "NNN", approx: isMG };
  }
  return { value: rate, basis: type || null, unconverted: true };
}

// Numeric value for sorting and totals, independent of how a cell renders.
function numericValue(col, pin, basis) {
  if (col.t === "norm") {
    const n = col.get(pin, basis);
    return n ? n.value : null;
  }
  return asNum(col.get(pin, basis));
}

function renderCell(col, pin, basis) {
  const v = col.get(pin, basis);
  switch (col.t) {
    case "num":   return showNum(v);
    case "money": return showMon(v);
    case "pct":   return showPct(v);
    case "psf":   return showPsf(v);
    case "date":  return showDate(v);
    case "norm": {
      const n = v;
      if (!n) return dash;
      const c = LEASE_COLORS[n.basis] || (n.basis === "MG" ? LEASE_COLORS.MG : "#6a6a6a");
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
          <span style={{ fontWeight: 700, color: "inherit" }}>
            ${n.value.toFixed(2)}{n.approx ? "*" : ""}
          </span>
          <span title={n.unconverted
              ? "No OPEX on file, so this rate could not be converted — shown on its own basis"
              : `Normalized to ${n.basis} basis`}
            style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.3, padding: "1px 5px", borderRadius: 3, background: `${c}22`, color: c, opacity: n.unconverted ? 1 : 0.75, border: n.unconverted ? `1px dashed ${c}66` : "none" }}>
            {n.basis || "?"}
          </span>
        </span>
      );
    }
    case "lease": {
      if (!v) return dash;
      const c = LEASE_COLORS[v] || "#6a6a6a";
      return (
        <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, background: `${c}22`, color: c, fontWeight: 800, fontSize: 10, letterSpacing: 0.3 }}>
          {v}
        </span>
      );
    }
    default: return showTxt(v);
  }
}

function sortValue(col, pin, basis) {
  if (isNumericCol(col)) {
    const n = numericValue(col, pin, basis);
    return n == null ? -Infinity : n;   // nulls sort last on descending
  }
  return String(col.get(pin, basis) ?? "").toLowerCase();
}

function loadHiddenCols() {
  try {
    const raw = localStorage.getItem(COLS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out = {};
    Object.keys(parsed || {}).forEach(k => { out[k] = new Set(parsed[k] || []); });
    return out;
  } catch { return {}; }
}

export default function MapListView({
  pins,
  comps,
  S,
  boundaryLabel,
  selectedCompIds,
  setSelectedCompIds,
  onFocusPin,
  onOpenDeal,
  onClose,
}) {
  // Opens on Deals — the active pipeline is the default question this view
  // answers. Terminal stages are off by default in the layer set.
  const [tabId, setTabId] = useState("deals");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [hiddenCols, setHiddenCols] = useState(loadHiddenCols);
  // Which basis lease rates are normalized onto. NNN by default, since that
  // is how industrial is quoted.
  const [basis, setBasis] = useState("nnn");   // "nnn" | "gross"
  const [colsOpen, setColsOpen] = useState(false);
  const colsRef = useRef(null);
  // Selection for non-comp rows. Comp rows read and write selectedCompIds
  // instead, so ticking one here lights up its pin and feeds the existing
  // bulk-apply action bar on the map.
  const [localSel, setLocalSel] = useState(() => new Set());
  // Clicking a row opens rent roll + nearby lease comps beneath it: what
  // this building is signing, against what its neighbours are signing.
  const [expandedId, setExpandedId] = useState(null);
  const [rentRollById, setRentRollById] = useState({});   // dealId -> rows | "loading"

  const tab = TABS.find(t => t.id === tabId) || TABS[0];
  const hiddenForTab = hiddenCols[tabId] || new Set();
  // The first column is the sticky anchor and can't be hidden — without it a
  // horizontally scrolled row has nothing identifying it.
  const cols = tab.cols.filter((c, i) => i === 0 || !hiddenForTab.has(c.key));

  useEffect(() => {
    try {
      const plain = {};
      Object.keys(hiddenCols).forEach(k => { plain[k] = [...hiddenCols[k]]; });
      localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(plain));
    } catch { /* private mode — column prefs just won't persist */ }
  }, [hiddenCols]);

  useEffect(() => {
    if (!colsOpen) return;
    function onDown(e) {
      if (colsRef.current && !colsRef.current.contains(e.target)) setColsOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [colsOpen]);

  const counts = useMemo(() => {
    const out = {};
    TABS.forEach(t => { out[t.id] = 0; });
    (pins || []).forEach(p => {
      const hit = TABS.find(t => t.match(p));
      if (hit) out[hit.id] += 1;
    });
    return out;
  }, [pins]);

  const rows = useMemo(() => {
    const qLc = query.trim().toLowerCase();
    let list = (pins || []).filter(tab.match);
    if (qLc) {
      list = list.filter(p =>
        tab.cols.some(c => c.t !== "norm" && String(c.get(p) ?? "").toLowerCase().includes(qLc)));
    }
    if (sort.key) {
      const col = tab.cols.find(c => c.key === sort.key);
      if (col) {
        const dir = sort.dir === "asc" ? 1 : -1;
        list = [...list].sort((a, b) => {
          const va = sortValue(col, a, basis), vb = sortValue(col, b, basis);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });
      }
    }
    return list;
  }, [pins, tab, query, sort, basis]);

  // Reset paging whenever the row set underneath it changes shape.
  useEffect(() => { setPage(0); setExpandedId(null); }, [tabId, query, sort, pins]);

  // Rent roll is fetched once per deal, the first time its row is opened.
  useEffect(() => {
    if (!expandedId) return;
    const pin = (pins || []).find(p => p.id === expandedId);
    const dealId = pin?.isPipeline ? pin.dealId : null;
    if (!dealId || rentRollById[dealId] !== undefined) return;
    let cancelled = false;
    setRentRollById(prev => ({ ...prev, [dealId]: "loading" }));
    loadRentRoll(dealId).then(rows => {
      if (!cancelled) setRentRollById(prev => ({ ...prev, [dealId]: rows || [] }));
    });
    return () => { cancelled = true; };
  }, [expandedId, pins, rentRollById]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const isComps = tab.id === "sale" || tab.id === "lease";
  // Active Deals rows open in place — rent roll against neighbouring lease
  // comps is the reason to be looking at a deal here. Every other tab has
  // no rent roll of its own, so its rows go to the property page instead.
  const rowsExpand = tab.id === "deals";

  function isSelected(pin) {
    return isComps ? !!selectedCompIds?.has(pin._rowId) : localSel.has(pin.id);
  }

  function toggleRow(pin) {
    if (isComps) {
      setSelectedCompIds(prev => {
        const next = new Set(prev);
        if (next.has(pin._rowId)) next.delete(pin._rowId); else next.add(pin._rowId);
        return next;
      });
    } else {
      setLocalSel(prev => {
        const next = new Set(prev);
        if (next.has(pin.id)) next.delete(pin.id); else next.add(pin.id);
        return next;
      });
    }
  }

  const allOnPageSelected = pageRows.length > 0 && pageRows.every(isSelected);

  function togglePage() {
    const target = !allOnPageSelected;
    const apply = (prev, idOf) => {
      const next = new Set(prev);
      pageRows.forEach(p => { if (target) next.add(idOf(p)); else next.delete(idOf(p)); });
      return next;
    };
    if (isComps) setSelectedCompIds(prev => apply(prev, p => p._rowId));
    else setLocalSel(prev => apply(prev, p => p.id));
  }

  const selectedRows = rows.filter(isSelected);
  const selCount = selectedRows.length;

  // Footer aggregates. Sums ignore nulls; averages ignore them too rather than
  // counting them as zero, which would drag every average down.
  const totals = useMemo(() => {
    const base = selCount > 0 ? selectedRows : rows;
    return (tab.totals || []).map(t => {
      const col = tab.cols.find(c => c.key === t.key);
      const nums = base
        .map(p => (col ? numericValue(col, p, basis) : asNum(pinField(p, t.key))))
        .filter(n => n != null);
      if (nums.length === 0) return { label: t.label, value: dash };
      const sum = nums.reduce((a, b) => a + b, 0);
      return { label: t.label, value: t.fmt(t.kind === "avg" ? sum / nums.length : sum) };
    });
  }, [rows, selectedRows, selCount, tab, basis]);

  function applyPreset(keys) {
    if (!keys) {
      setHiddenCols(prev => ({ ...prev, [tabId]: new Set() }));   // "All"
      return;
    }
    const keep = new Set(keys);
    const hide = tab.cols.filter((c, i) => i !== 0 && !keep.has(c.key)).map(c => c.key);
    setHiddenCols(prev => ({ ...prev, [tabId]: new Set(hide) }));
  }

  function toggleCol(key) {
    setHiddenCols(prev => {
      const cur = new Set(prev[tabId] || []);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      return { ...prev, [tabId]: cur };
    });
  }

  // Turns a tab plus a row list into the plain spec the styled workbook
  // builder wants. A normalized column contributes two spreadsheet columns —
  // the number and the basis it sits on — so the basis travels with the figure
  // instead of arriving in Excel as an unlabelled dollar amount.
  function buildSheetSpec(t, list) {
    const columns = [];
    const colIndexByKey = {};
    t.cols.forEach(c => {
      colIndexByKey[c.key] = columns.length;
      columns.push({ label: c.label, type: c.t });
      if (c.t === "norm") columns.push({ label: `${c.label} Basis`, type: "text" });
    });
    columns.push({ label: "Latitude", type: "num" }, { label: "Longitude", type: "num" });

    const data = list.map(p => {
      const row = [];
      t.cols.forEach(c => {
        if (c.t === "norm") {
          const n = c.get(p, basis);
          row.push(n ? Number(n.value.toFixed(2)) : null);
          row.push(n ? (n.basis || "") : "");
          return;
        }
        const v = c.get(p, basis);
        row.push(isNumericCol(c) ? asNum(v) : (v == null ? "" : String(v)));
      });
      row.push(asNum(p.lat), asNum(p.lng));
      return row;
    });

    const totals = (t.totals || [])
      .filter(tt => colIndexByKey[tt.key] !== undefined)
      .map(tt => ({ colIndex: colIndexByKey[tt.key], kind: tt.kind, label: tt.label }));

    return { name: t.label, columns, data, totals };
  }

  // Every tab as its own sheet, or just the ticked rows once anything is
  // selected. Always writes the full column set regardless of what's hidden on
  // screen — hiding a column is about reading, not discarding data.
  function exportXLSX() {
    const specs = selCount > 0
      ? [buildSheetSpec(tab, selectedRows)]
      : TABS.map(t => {
          const list = (pins || []).filter(t.match);
          return list.length > 0 ? buildSheetSpec(t, list) : null;
        }).filter(Boolean);

    const stamp = new Date().toISOString().slice(0, 10);
    const slug = boundaryLabel
      ? `_${boundaryLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`
      : "";
    downloadMapWorkbook(specs, `proxy-map-${stamp}${slug}${selCount > 0 ? "_selection" : ""}.xlsx`);
  }

  const btn = (active) => ({
    background: active ? S.red : "transparent",
    border: `1px solid ${active ? S.red : S.border}`,
    borderRadius: 6,
    color: active ? "#ffffff" : S.text,
    padding: "5px 11px",
    fontSize: 11,
    fontWeight: 700,
    fontFamily: S.font,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  const anchorLeft = CHECKBOX_W;

  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 700, background: S.dark,
      display: "flex", flexDirection: "column", fontFamily: S.font, color: S.text,
      // Consumed by the stylesheet below so hover and sticky backgrounds stay
      // in sync with light/dark without duplicating the palette in JS.
      "--mlv-bg": S.dark,
      "--mlv-hover": S.card,
      "--mlv-sel": "rgba(233,53,41,0.10)",
    }}>
      <style>{`
        .mlv-table { border-collapse: separate; border-spacing: 0; font-size: 11.5px; white-space: nowrap; }
        .mlv-row > td { background: var(--mlv-bg); transition: background 0.08s; }
        .mlv-row:hover > td { background: var(--mlv-hover); }
        .mlv-row.mlv-sel > td { background: var(--mlv-sel); }
        .mlv-sticky { position: sticky; z-index: 1; }
        .mlv-table thead th { position: sticky; top: 0; z-index: 2; }
        .mlv-table thead th.mlv-sticky { z-index: 3; }
        .mlv-anchor { text-decoration: underline dotted; text-underline-offset: 3px; }
        .mlv-anchor:hover { color: #e93529 !important; }
      `}</style>

      {/* ── Entity tabs + tools ─────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderBottom: `1px solid ${S.border}`, background: S.sidebar, flexShrink: 0, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {TABS.map(t => {
            const active = t.id === tabId;
            return (
              <button key={t.id} onClick={() => setTabId(t.id)}
                style={{ ...btn(active), display: "flex", alignItems: "center", gap: 6 }}>
                {t.label}
                <span style={{ fontSize: 10, fontWeight: 600, color: active ? "rgba(255,255,255,0.82)" : S.muted }}>
                  {counts[t.id].toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {selCount > 0 && (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, color: S.red }}>
                {selCount.toLocaleString()} selected
              </span>
              <button onClick={() => (isComps ? setSelectedCompIds(new Set()) : setLocalSel(new Set()))}
                style={{ background: "none", border: "none", color: S.muted, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: S.font, textDecoration: "underline" }}>
                clear
              </button>
            </>
          )}

          {tab.id === "lease" && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color: S.muted }}>Basis</span>
              <div style={{ display: "flex", background: S.card, border: `1px solid ${S.border}`, borderRadius: 6, overflow: "hidden" }}>
                {[["nnn", "NNN"], ["gross", "Gross"]].map(([v, label]) => (
                  <button key={v} onClick={() => setBasis(v)}
                    title={v === "nnn"
                      ? "Net rates: Gross comps have OPEX deducted where it is on file"
                      : "Gross rates: NNN comps have OPEX added where it is on file"}
                    style={{ border: "none", background: basis === v ? S.red : "transparent", color: basis === v ? "#ffffff" : S.muted, padding: "5px 10px", fontSize: 10, fontWeight: 800, fontFamily: S.font, cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter this tab..."
            style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 6, padding: "6px 9px", fontSize: 11, color: S.text, fontFamily: S.font, outline: "none", width: 160 }} />

          {/* Column chooser */}
          <div ref={colsRef} style={{ position: "relative" }}>
            <button onClick={() => setColsOpen(o => !o)} style={btn(colsOpen)}>
              ⚙ Columns
              {hiddenForTab.size > 0 && (
                <span style={{ marginLeft: 5, fontWeight: 600, color: colsOpen ? "rgba(255,255,255,0.8)" : S.muted }}>
                  {cols.length}/{tab.cols.length}
                </span>
              )}
            </button>
            {colsOpen && (
              <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 250, maxHeight: 420, overflowY: "auto", background: S.sidebar, border: `1px solid ${S.border}`, borderRadius: 9, boxShadow: "0 8px 28px rgba(0,0,0,0.4)", zIndex: 40, padding: 10 }}>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: S.muted, marginBottom: 7 }}>Presets</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12 }}>
                  <button onClick={() => applyPreset(null)}
                    style={{ ...btn(hiddenForTab.size === 0), padding: "4px 9px", fontSize: 10 }}>All</button>
                  {Object.keys(tab.presets || {}).map(name => (
                    <button key={name} onClick={() => applyPreset(tab.presets[name])}
                      style={{ ...btn(false), padding: "4px 9px", fontSize: 10 }}>{name}</button>
                  ))}
                </div>
                <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: S.muted, marginBottom: 7 }}>Show</div>
                {tab.cols.map((c, i) => {
                  const locked = i === 0;
                  const on = locked || !hiddenForTab.has(c.key);
                  return (
                    <label key={c.key}
                      title={locked ? "The first column stays visible — it anchors the row when you scroll sideways" : undefined}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", fontSize: 11.5, color: locked ? S.muted : S.text, cursor: locked ? "default" : "pointer" }}>
                      <input type="checkbox" checked={on} disabled={locked}
                        onChange={() => toggleCol(c.key)}
                        style={{ accentColor: "#e93529", cursor: locked ? "default" : "pointer" }} />
                      {c.label}{locked && <span style={{ fontSize: 9, marginLeft: "auto" }}>pinned</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <button onClick={exportXLSX} disabled={rows.length === 0}
            style={{ ...btn(true), opacity: rows.length === 0 ? 0.5 : 1, cursor: rows.length === 0 ? "not-allowed" : "pointer" }}>
            ↓ Excel{selCount > 0 ? ` (${selCount})` : ""}
          </button>
          <button onClick={onClose} style={btn(false)}>Back to map</button>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {rows.length === 0 ? (
          <div style={{ padding: 50, textAlign: "center", fontSize: 12.5, color: S.muted }}>
            Nothing in this tab. Widen the boundary, turn on more layers, or clear the filter.
          </div>
        ) : (
          <table className="mlv-table">
            <thead>
              <tr>
                <th className="mlv-sticky" style={{ left: 0, background: S.card, borderBottom: `1px solid ${S.border}`, padding: 0, width: CHECKBOX_W, minWidth: CHECKBOX_W, textAlign: "center" }}>
                  <input type="checkbox" checked={allOnPageSelected} onChange={togglePage}
                    title="Select everything on this page"
                    style={{ accentColor: "#e93529", cursor: "pointer" }} />
                </th>
                {cols.map((c, ci) => {
                  const active = sort.key === c.key;
                  const numeric = isNumericCol(c);
                  const isAnchor = ci === 0;
                  return (
                    <th key={c.key}
                      className={isAnchor ? "mlv-sticky" : undefined}
                      onClick={() => setSort(prev => prev.key === c.key
                        ? { key: c.key, dir: prev.dir === "asc" ? "desc" : "asc" }
                        : { key: c.key, dir: numeric ? "desc" : "asc" })}
                      style={{
                        left: isAnchor ? anchorLeft : undefined,
                        background: S.card,
                        borderBottom: `1px solid ${S.border}`,
                        borderRight: isAnchor ? `1px solid ${S.border}` : undefined,
                        padding: "9px 12px", minWidth: c.w,
                        textAlign: numeric ? "right" : "left",
                        fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: active ? S.red : S.muted,
                        cursor: "pointer", userSelect: "none",
                      }}>
                      {c.label}{active ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p, i) => {
                const sel = isSelected(p);
                return (
                  <>
                  <tr key={`${p.id}-${i}`} className={`mlv-row${sel ? " mlv-sel" : ""}`}
                    onClick={() => (rowsExpand
                      ? setExpandedId(cur => (cur === p.id ? null : p.id))
                      : onFocusPin && onFocusPin(p))}
                    style={{ cursor: "pointer" }}>
                    <td className="mlv-sticky" style={{ left: 0, padding: 0, width: CHECKBOX_W, minWidth: CHECKBOX_W, textAlign: "center", borderBottom: `1px solid ${S.border}` }}>
                      <input type="checkbox" checked={sel} onChange={() => toggleRow(p)}
                        style={{ accentColor: "#e93529", cursor: "pointer" }} />
                    </td>
                    {cols.map((c, ci) => {
                      const numeric = isNumericCol(c);
                      const isAnchor = ci === 0;
                      return (
                        <td key={c.key}
                          className={isAnchor ? "mlv-sticky" : undefined}
                          title={isAnchor
                            ? (rowsExpand ? "Open rent roll and nearby lease comps" : "Open the property page")
                            : undefined}
                          style={{
                            left: isAnchor ? anchorLeft : undefined,
                            borderBottom: `1px solid ${S.border}`,
                            borderRight: isAnchor ? `1px solid ${S.border}` : undefined,
                            padding: "11px 12px",
                            textAlign: numeric ? "right" : "left",
                            fontVariantNumeric: numeric ? "tabular-nums" : "normal",
                            color: isAnchor ? S.text : S.muted,
                            fontWeight: isAnchor ? 700 : 400,
                            cursor: "pointer",
                            maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis",
                          }}>
                          {isAnchor
                            ? <span className="mlv-anchor">{renderCell(c, p, basis)}</span>
                            : c.key === "layerKey"
                              ? (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: MAP_LAYER_COLORS[p.layerKey] || "#6a6a6a", flexShrink: 0 }} />
                                  {renderCell(c, p, basis)}
                                </span>
                              )
                              : renderCell(c, p, basis)}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedId === p.id && (
                    <tr key={`${p.id}-${i}-x`}>
                      <td colSpan={cols.length + 1} style={{ padding: 0, background: S.dark, borderBottom: `2px solid ${S.red}` }}>
                        <ExpandedRow pin={p} comps={comps}
                          rentRoll={p.isPipeline ? rentRollById[p.dealId] : undefined}
                          S={S} onOpenDeal={onOpenDeal} />
                      </td>
                    </tr>
                  )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer: totals + paging ─────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "9px 14px", borderTop: `1px solid ${S.border}`, background: S.sidebar, flexShrink: 0, fontSize: 11, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 800 }}>
          {rows.length.toLocaleString()} {tab.label.toLowerCase()}
          {selCount > 0 && <span style={{ color: S.red }}> · {selCount.toLocaleString()} selected</span>}
        </span>
        {totals.map(t => (
          <span key={t.label} style={{ color: S.muted }}>
            {t.label} <strong style={{ color: S.text, fontVariantNumeric: "tabular-nums" }}>{t.value}</strong>
          </span>
        ))}
        {selCount > 0 && <span style={{ fontSize: 10, color: S.muted }}>(totals reflect selection)</span>}
        {tab.id === "lease" && (
          <span style={{ fontSize: 10, color: S.muted }}>
            Norm = {basis === "nnn" ? "net of OPEX" : "OPEX included"}; dashed badge = no OPEX on file, shown on its own basis; * = Modified Gross, approximate
          </span>
        )}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: S.muted, fontVariantNumeric: "tabular-nums" }}>
            {(safePage * PAGE_SIZE + 1).toLocaleString()}–{Math.min((safePage + 1) * PAGE_SIZE, rows.length).toLocaleString()} of {rows.length.toLocaleString()}
          </span>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}
            style={{ ...btn(false), opacity: safePage === 0 ? 0.4 : 1, cursor: safePage === 0 ? "not-allowed" : "pointer", padding: "4px 10px" }}>‹</button>
          <span style={{ color: S.muted, fontVariantNumeric: "tabular-nums" }}>{safePage + 1} / {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}
            style={{ ...btn(false), opacity: safePage >= pageCount - 1 ? 0.4 : 1, cursor: safePage >= pageCount - 1 ? "not-allowed" : "pointer", padding: "4px 10px" }}>›</button>
        </div>
      </div>
    </div>
  );
}

// ── Expanded row: what this building signs vs what its neighbours sign ─────
function ExpandedRow({ pin, comps, rentRoll, S, onOpenDeal }) {
  const nearby = leaseCompsNear(comps, pin);
  const rrRows = Array.isArray(rentRoll) ? rentRoll : [];
  const rrLoading = rentRoll === "loading";

  // Both tables in one workbook, so the in-place / market comparison survives
  // the trip to Excel.
  function exportBoth() {
    const label = (pin._dealName || pin.address || "deal").toString();
    const sheets = [];
    if (rrRows.length > 0) {
      sheets.push({
        name: "Rent Roll",
        columns: [
          { label: "Suite", type: "text" }, { label: "Tenant", type: "text" },
          { label: "Suite SF", type: "num" }, { label: "Rate $/SF", type: "psf" },
          { label: "Lease Type", type: "text" }, { label: "Start", type: "text" },
          { label: "End", type: "text" }, { label: "WALT (yrs)", type: "num" },
        ],
        data: rrRows.map(r => [
          r.suite_id ?? "", r.tenant ?? "", asNum(r.suite_sf), asNum(r.lease_rate),
          r.lease_type ?? "", r.lease_start ?? "", r.lease_end ?? "", asNum(r.walt_years),
        ]),
        totals: [{ colIndex: 2, kind: "sum" }, { colIndex: 3, kind: "avg" }],
      });
    }
    if (nearby.length > 0) {
      sheets.push({
        name: "Nearby Lease Comps",
        columns: [
          { label: "Date", type: "text" }, { label: "Address", type: "text" },
          { label: "Tenant", type: "text" }, { label: "Building SF", type: "num" },
          { label: "Suite SF", type: "num" }, { label: "Rate $/SF", type: "psf" },
          { label: "Lease Type", type: "text" }, { label: "OPEX $/SF", type: "psf" },
          { label: "Landlord", type: "text" }, { label: "Source", type: "text" },
        ],
        data: nearby.map(c => [
          c.comp_date ?? "", c.address ?? "", c.tenant ?? "", asNum(c.building_sf),
          asNum(c.suite_sf), asNum(c.lease_rate), c.lease_type ?? "",
          asNum(c.opex_psf), c.landlord ?? "", c.source ?? "",
        ]),
        totals: [{ colIndex: 4, kind: "sum" }, { colIndex: 5, kind: "avg" }],
      });
    }
    if (sheets.length === 0) return;
    const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
    downloadMapWorkbook(sheets, `proxy-${slug || "deal"}-rentroll-comps-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  const actionBtn = (primary) => ({
    background: primary ? S.red : "transparent",
    border: `1px solid ${primary ? S.red : S.border}`,
    borderRadius: 7, color: primary ? "#ffffff" : S.text,
    padding: "6px 12px", fontSize: 10.5, fontWeight: 800,
    fontFamily: S.font, cursor: "pointer", whiteSpace: "nowrap",
  });

  const wrap = { padding: "12px 14px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 22 };
  const h = { fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: S.red, marginBottom: 6 };
  const th = { textAlign: "left", padding: "5px 10px 5px 0", fontSize: 9, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: S.muted, borderBottom: `1px solid ${S.border}`, whiteSpace: "nowrap" };
  const td = { padding: "6px 10px 6px 0", borderBottom: `1px solid ${S.border}`, fontSize: 11, whiteSpace: "nowrap" };

  const canExport = rrRows.length > 0 || nearby.length > 0;

  return (
    <div onClick={e => e.stopPropagation()}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 14px 0" }}>
        {pin.isPipeline && onOpenDeal && pin.dealId && (
          <button onClick={() => onOpenDeal(pin.dealId)} style={actionBtn(true)}>
            View Deal Page →
          </button>
        )}
        <button onClick={exportBoth} disabled={!canExport}
          style={{ ...actionBtn(false), opacity: canExport ? 1 : 0.5, cursor: canExport ? "pointer" : "not-allowed" }}>
          ↓ Export rent roll + comps
        </button>
      </div>
      <div style={wrap}>
      <div>
        <div style={h}>Rent roll — what this building is signing</div>
        {!pin.isPipeline ? (
          <div style={{ fontSize: 11, color: S.muted }}>Rent roll is recorded against deals only.</div>
        ) : rrLoading ? (
          <div style={{ fontSize: 11, color: S.muted }}>Loading…</div>
        ) : rrRows.length === 0 ? (
          <div style={{ fontSize: 11, color: S.muted }}>No rent roll captured for this deal.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Suite", "Tenant", "SF", "Rate $/SF", "Type", "Start", "End"].map(x => <th key={x} style={th}>{x}</th>)}
              </tr></thead>
              <tbody>
                {rrRows.map((r, i) => (
                  <tr key={r.id || i}>
                    <td style={td}>{showTxt(r.suite_id)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{showTxt(r.tenant)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{showNum(r.suite_sf)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{showPsf(r.lease_rate)}</td>
                    <td style={td}>{showTxt(r.lease_type)}</td>
                    <td style={td}>{showDate(r.lease_start)}</td>
                    <td style={td}>{showDate(r.lease_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <div style={h}>Lease comps nearby — what others are signing</div>
        {nearby.length === 0 ? (
          <div style={{ fontSize: 11, color: S.muted }}>No lease comps at this address or within 200m.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Date", "Address", "Tenant", "SF", "Rate $/SF", "Type"].map(x => <th key={x} style={th}>{x}</th>)}
              </tr></thead>
              <tbody>
                {nearby.slice(0, 25).map((c, i) => (
                  <tr key={c.id || i}>
                    <td style={td}>{showDate(c.comp_date)}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{showTxt(c.address)}</td>
                    <td style={td}>{showTxt(c.tenant)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{showNum(c.suite_sf ?? c.building_sf)}</td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{showPsf(c.lease_rate)}</td>
                    <td style={td}>{showTxt(c.lease_type)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {nearby.length > 25 && (
              <div style={{ fontSize: 9.5, color: S.muted, marginTop: 5 }}>
                showing 25 of {nearby.length} — open the property page for the full set
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
